import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/auth/AuthProvider';
import { Workbench } from '@/components/layout/Workbench';
import { LoadingState } from '@/components/common/states';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ApiError } from '@/api/client';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // A 401 is handled by the auth layer; retrying just burns time.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
        return failureCount < 2;
      },
    },
  },
});

function SignInForm(): JSX.Element {
  const { error, busy, realm, signInWithPassword } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  return (
    <form
      className="mt-4 space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        void signInWithPassword?.(username, password);
      }}
    >
      <label className="block">
        <span className="eyebrow">Username</span>
        <input
          className="field mt-0.5"
          autoComplete="username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          required
        />
      </label>
      <label className="block">
        <span className="eyebrow">Password</span>
        <input
          className="field mt-0.5"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
      </label>

      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        className="btn btn-run w-full justify-center"
        disabled={busy || username === '' || password === ''}
      >
        {busy ? 'Signing in…' : 'Sign in'}
      </button>

      {realm ? (
        <p className="text-[10px] leading-4 text-faint">
          Directory: <span className="font-mono text-muted">{realm}</span>
        </p>
      ) : null}

      <p className="text-[10px] leading-4 text-faint">
        Your token is held in memory only, so signing in again is needed after a reload.
      </p>
    </form>
  );
}

function Gate(): JSX.Element {
  const { status, error, signIn, signInWithPassword } = useAuth();

  if (status === 'loading') {
    return <LoadingState label="Loading sign-in settings" />;
  }

  // Config could not be loaded. Say so plainly rather than offering a sign-in
  // form we have no settings for -- and never quietly drop to a permissive mode.
  if (status === 'error') {
    return (
      <div className="flex h-full items-center justify-center bg-base p-6">
        <div className="w-96 rounded-sm border border-danger/50 bg-danger-dim p-5" role="alert">
          <span className="eyebrow text-danger">Sign-in unavailable</span>
          <p className="mt-2 font-mono text-xs leading-5 text-ink">{error}</p>
          <p className="mt-2 text-xs text-muted">
            The workbench reads its realm from the service at
            <code className="mx-1 font-mono">/api/v1/auth/config</code>. Check that the service
            is reachable, then try again.
          </p>
          <button type="button" className="btn btn-run mt-4 w-full justify-center" onClick={signIn}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (status === 'authenticated') {
    // Last line of defence. The per-cell and per-panel boundaries inside should
    // catch anything real; this one just guarantees no white page.
    return (
      <ErrorBoundary label="Workbench">
        <Workbench />
      </ErrorBoundary>
    );
  }

  return (
    <div className="flex h-full items-center justify-center bg-base p-6">
      <div className="w-80 rounded-sm border border-line bg-surface p-5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-md font-semibold tracking-tight text-ink">DuckDB</span>
          <span className="eyebrow">Workbench</span>
        </div>
        <p className="mt-2 text-xs text-muted">
          Sign in with your iohealth account to query the lake.
        </p>
        {signInWithPassword ? (
          <SignInForm />
        ) : (
          <>
            {error ? (
              <p className="mt-2 text-xs text-danger" role="alert">
                {error}
              </p>
            ) : null}
            <button
              type="button"
              className="btn btn-run mt-4 w-full justify-center"
              onClick={signIn}
            >
              Sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </QueryClientProvider>
  );
}
